import { type SVGProps, useId } from 'react';
import { clsx } from 'clsx';
import styles from './interchange-logomark.module.scss';

interface InterchangeLogomarkProps extends SVGProps<SVGSVGElement> {
  fill?: 'gradient' | 'currentColor';
  withShadow?: boolean;
}

export function InterchangeLogomark({ className, fill = 'gradient', id: providedId, withShadow, ...props }: InterchangeLogomarkProps) {
  const generatedId = useId();
  const id = providedId || generatedId;
  const isGradient = fill === 'gradient';

  return (
    <svg
      className={clsx(styles.interchangeLogomark, className)}
      fill="none"
      id={id}
      viewBox="0 0 959 1411"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <title>Interchange Logomark</title>

      <g filter={withShadow ? `url(#${id}_shadow)` : undefined}>
        <path
          clipRule="evenodd"
          d="M265.938 1038.07C19.4625 894.505 17.671 536.224 262.698 390.157L776.17 84.0627C789.923 75.8644 807.659 80.4659 815.786 94.3406C823.912 108.215 819.351 126.109 805.598 134.307L292.125 440.402C85.2139 563.747 86.7268 866.296 294.861 987.528L777.586 1268.7C791.42 1276.76 809.109 1271.98 817.096 1258.02C825.083 1244.06 820.344 1226.22 806.51 1218.16C792.676 1210.1 787.936 1192.26 795.923 1178.3C803.91 1164.34 821.599 1159.56 835.433 1167.62C876.935 1191.79 891.155 1245.33 867.194 1287.2C843.233 1329.07 790.164 1343.42 748.662 1319.24L265.938 1038.07Z"
          fill={isGradient ? `url(#${id}_fill1)` : 'currentColor'}
          fillRule="evenodd"
        />
        <path
          clipRule="evenodd"
          d="M358.086 528.088C219.991 608.101 219.763 809.008 357.677 889.338L835.434 1167.62C876.936 1191.79 891.155 1245.33 867.194 1287.2C843.233 1329.07 790.164 1343.42 748.663 1319.24C734.829 1311.19 730.089 1293.34 738.076 1279.38C746.063 1265.43 763.752 1260.64 777.586 1268.7C791.42 1276.76 809.11 1271.98 817.097 1258.02C825.084 1244.06 820.344 1226.22 806.51 1218.16L328.753 939.88C152.224 837.057 152.515 579.896 329.277 477.48L747.973 234.887C790.116 210.469 843.857 225.853 867.239 268.652C890.02 310.355 875.657 362.998 834.635 386.853C820.796 394.902 803.109 390.107 795.132 376.145C787.154 362.183 791.907 344.34 805.746 336.291C819.333 328.39 824.219 310.812 816.576 296.822C808.731 282.462 790.738 277.41 776.782 285.496L358.086 528.088Z"
          fill={isGradient ? `url(#${id}_fill2)` : 'currentColor'}
          fillRule="evenodd"
        />
        <path
          clipRule="evenodd"
          d="M387.608 839.385C288.401 781.6 288.452 637.121 387.698 579.405L805.744 336.293C819.331 328.392 824.217 310.813 816.574 296.823C808.73 282.463 790.738 277.412 776.782 285.498C762.93 293.524 745.251 288.701 737.296 274.726C729.34 260.751 734.121 242.916 747.973 234.89C790.116 210.472 843.855 225.853 867.237 268.654C890.019 310.356 875.655 362.999 834.633 386.855L416.587 629.967C355.937 665.238 355.906 753.53 416.532 788.843L864.103 1049.54C877.937 1057.6 882.677 1075.44 874.69 1089.4C866.702 1103.36 849.013 1108.14 835.179 1100.08L387.608 839.385Z"
          fill={isGradient ? `url(#${id}_fill3)` : 'currentColor'}
          fillRule="evenodd"
        />
      </g>
      <defs>
        {withShadow ? (
          <filter colorInterpolationFilters="sRGB" filterUnits="userSpaceOnUse" height="100%" id={`${id}_shadow`} width="100%" x="-0.000366211" y="0">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix in="SourceAlpha" result="hardAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
            <feOffset dx="40" dy="40" />
            <feGaussianBlur stdDeviation="20" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0.788235 0 0 0 0 0.294118 0 0 0 0 0.717647 0 0 0 0.2 0" />
            <feBlend in2="BackgroundImageFix" mode="normal" result="effect1_dropShadow_63_634" />
            <feColorMatrix in="SourceAlpha" result="hardAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
            <feOffset dx="-40" dy="-40" />
            <feGaussianBlur stdDeviation="20" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0.207843 0 0 0 0 0.784314 0 0 0 0 0.803922 0 0 0 0.1 0" />
            <feBlend in2="effect1_dropShadow_63_634" mode="normal" result="effect2_dropShadow_63_634" />
            <feBlend in="SourceGraphic" in2="effect2_dropShadow_63_634" mode="normal" result="shape" />
            <feColorMatrix in="SourceAlpha" result="hardAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
            <feOffset dy="4" />
            <feGaussianBlur stdDeviation="1" />
            <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
            <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0" />
            <feBlend in2="shape" mode="normal" result="effect3_innerShadow_63_634" />
          </filter>
        ) : null}

        <linearGradient gradientUnits="userSpaceOnUse" id={`${id}_fill1`} x1="850.354" x2="850.354" y1="1301.52" y2="109.294">
          <stop className={styles.stop1_0} />
          <stop className={styles.stop1_1} offset="1" />
        </linearGradient>
        <linearGradient gradientUnits="userSpaceOnUse" id={`${id}_fill2`} x1="850.353" x2="850.353" y1="1301.52" y2="251.027">
          <stop className={styles.stop2_0} />
          <stop className={styles.stop2_1} offset="1" />
        </linearGradient>
        <linearGradient gradientUnits="userSpaceOnUse" id={`${id}_fill3`} x1="850.355" x2="850.355" y1="1072.24" y2="251.027">
          <stop className={styles.stop3_0} />
          <stop className={styles.stop3_1} offset="1" />
        </linearGradient>
      </defs>
    </svg>
  );
}
